import React, { useState, useEffect } from 'react';
import { ErrorMessage } from 'formik';
import styled from 'styled-components';
import { Checkbox } from 'antd';
import errorReporter from '@app/errorReporter';
import { LockboxWithMlsNumber } from '@bb/domain/dist/types';

import API from 'api';
import Loading from '../react-common/Loading';
import { StyledTableDataLabel } from './styled';

type SerialNumberSelectProps = {
  inputName: string;
  selectedSerialNumbers: LockboxWithMlsNumber[];
  handleChange(value?: LockboxWithMlsNumber[]): void;
  userId: string;
  setLockboxLength: (length: number) => void;
};

const SerialNumberSelect: React.FC<SerialNumberSelectProps> = ({
  inputName,
  selectedSerialNumbers,
  handleChange,
  userId,
  setLockboxLength,
}) => {
  const [lockboxes, setLockboxes] = useState<LockboxWithMlsNumber[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    (async () => {
      try {
        const lockboxData = await API.GET.getLockboxesByAgent(userId);
        setLockboxes(lockboxData);
        setLockboxLength(lockboxData.length);

        // TODO: change 'sentrilock' for the future use with other brands
        const assignedNumbers = await API.GET.getSerialNumbersForUser(
          userId,
          'sentriLock',
        );
        handleChange(
          assignedNumbers.map(data => ({
            lbsn: data.serialNumber,
            mlsNumber: data.userMlsNumber,
          })),
        );
      } catch (err) {
        errorReporter.error(err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [userId]);

  const selectedNumbersMap = selectedSerialNumbers.reduce(
    (map, data) => {
      if (data.lbsn) {
        return {
          ...map,
          [data.lbsn]: true,
        };
      }
      return map;
    },
    {} as Record<string, boolean>,
  );

  const toggleLockbox = (lockbox: LockboxWithMlsNumber) => {
    let newSerialNumbers: LockboxWithMlsNumber[];
    if (selectedNumbersMap[lockbox.lbsn]) {
      newSerialNumbers = selectedSerialNumbers.filter(
        data => data.lbsn !== lockbox.lbsn,
      );
    } else {
      newSerialNumbers = [...selectedSerialNumbers, lockbox];
    }
    handleChange(newSerialNumbers);
  };

  return (
    <tr>
      <StyledTableDataLabel>
        <label>Available Boxes:</label>
        <br />
        <StyledSmallLabel>(serial numbers)</StyledSmallLabel>
      </StyledTableDataLabel>
      <td>
        {isLoading && <Loading size={'fa-sm'} noPadding />}
        {!isLoading &&
          (lockboxes.length ? (
            lockboxes.map(lockbox => (
              <StyledCheckGroup key={lockbox.lbsn}>
                <StyledCheckbox
                  onClick={() => toggleLockbox(lockbox)}
                  checked={selectedNumbersMap[lockbox.lbsn]}
                >
                  {lockbox.lbsn}
                </StyledCheckbox>
              </StyledCheckGroup>
            ))
          ) : (
            <label>No Lockboxes found for user</label>
          ))}
        <ErrorMessage name={inputName} component="label" className="error" />
      </td>
    </tr>
  );
};

export default SerialNumberSelect;

const StyledLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const StyledSmallLabel = styled.label`
  font-size: 11px;
`;

const StyledCheckGroup = styled.div`
  display: flex;
  align-items: center;
  margin: 0 0 10px 0;
`;

const StyledCheckbox = styled(Checkbox)`
  && {
    display: flex;
    justify-content: center;
  }
`;

const StyledSerial = styled.span`
  margin: 0 0 0 10px;
`;
